import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AssessmentOutlined as AssessmentOutlinedIcon,
  DirectionsCarFilledOutlined as DirectionsCarFilledOutlinedIcon,
  Groups2Outlined as Groups2OutlinedIcon,
  NotificationsActiveOutlined as NotificationsActiveOutlinedIcon,
  PersonOutlineRounded as PersonOutlineRoundedIcon,
  PlaceOutlined as PlaceOutlinedIcon,
  ReceiptLongOutlined as ReceiptLongOutlinedIcon,
  RouteOutlined as RouteOutlinedIcon,
  SettingsOutlined as SettingsOutlinedIcon,
  LocalShippingOutlined as LocalShippingOutlinedIcon,
  TaskAltOutlined as TaskAltOutlinedIcon,
  MarkUnreadChatAltOutlined as MarkUnreadChatAltOutlinedIcon,
  EventNoteOutlined as EventNoteOutlinedIcon,
  PaidOutlined as PaidOutlinedIcon,
  SensorsRounded as SensorsRoundedIcon,
} from "@mui/icons-material";
import GlobalDashboardSidebar from "../admin/GlobalDashboardSidebar";
import useGlobalSidebarItems from "../admin/useGlobalSidebar";
import LogisticsDashboard from "../LogisticsDashboard";
import { USER_ROLE_ROUTE_CONFIG } from "../users/userRoleConfig";
import { logout } from "../../store/authSlice";
import "../admin/dashboard-redesign.css";

const LOGISTIC_MENU_ITEMS = [
  { key: "trips", label: "Trips" },
  { key: "clients", label: "Clients" },
  { key: "materials", label: "Materials" },
  { key: "documents", label: "Documents" },
];

const TASK_MENU_ITEMS = [
  { key: "all", label: "All Tasks" },
  { key: "create", label: "Create Task" },
  { key: "inventory", label: "Inventory" },
];

const MESSAGE_MENU_ITEMS = [
  { key: "compose", label: "Compose & Send" },
  { key: "templates", label: "Templates" },
  { key: "delivery-reports", label: "Delivery Reports" },
  { key: "scheduled", label: "Scheduled Messages" },
];

const EXPENSE_MENU_ITEMS = [
  { key: "create", label: "Create Expense", path: "/expense/create" },
  { key: "list", label: "Expense List", path: "/expense/list" },
  { key: "upload", label: "Upload Expense", path: "/expense/upload" },
];

const SENSOR_MENU_ITEMS = [
  { key: "list", label: "Sensor List", path: "/sensors/list" },
  { key: "create", label: "Add Sensor", path: "/sensors/create" },
  { key: "upload", label: "Upload Sensors", path: "/sensors/upload" },
];

const META = {
  trips: { title: "Trips", subtitle: "Manage trip routes, distance, duration, drivers and client deliveries" },
  clients: { title: "Clients", subtitle: "Manage client details, contacts, VAT and map location" },
  materials: { title: "Materials", subtitle: "Manage material categories, rates and active status" },
  documents: { title: "Documents", subtitle: "Manage logistic documents and uploaded files" },
};

export default function LogisticsModulePage({ section = "trips" }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useSelector((state) => state.auth.user);

  const profileName = currentUser?.name || currentUser?.username || "User";
  const roleLabel = String(currentUser?.role || "User")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

  const sectionMeta = META[section] || META.trips;

  const sidebarItemsLegacy = useMemo(
    () => [
      { key: "dashboard", label: "Dashboard", icon: <AssessmentOutlinedIcon fontSize="small" />, active: location.pathname === "/dashboard", onClick: () => navigate("/dashboard") },
      { key: "vehicles", label: "Vehicles", icon: <DirectionsCarFilledOutlinedIcon fontSize="small" />, active: location.pathname === "/admin-dashboard" && location.search.includes("tab=devices"), onClick: () => navigate("/admin-dashboard?tab=devices") },
      { key: "live-tracking", label: "Live Tracking", icon: <PlaceOutlinedIcon fontSize="small" />, active: location.pathname === "/admin-dashboard" && location.search.includes("tab=map"), onClick: () => navigate("/admin-dashboard?tab=map") },
      { key: "trips", label: "Trips", icon: <RouteOutlinedIcon fontSize="small" />, active: location.pathname === "/logistic/trips", onClick: () => navigate("/logistic/trips") },
      { key: "geofences", label: "Geofences", icon: <PlaceOutlinedIcon fontSize="small" />, active: location.pathname === "/geofences", onClick: () => navigate("/geofences") },
      { key: "notifications", label: "Notifications", icon: <NotificationsActiveOutlinedIcon fontSize="small" />, active: location.pathname === "/notifications", onClick: () => navigate("/notifications") },
      { key: "reports", label: "Reports", icon: <ReceiptLongOutlinedIcon fontSize="small" />, active: location.pathname === "/reports", onClick: () => navigate("/reports") },
      {
        key: "tasks",
        label: "Tasks",
        icon: <TaskAltOutlinedIcon fontSize="small" />,
        active: location.pathname.startsWith("/tasks"),
        children: TASK_MENU_ITEMS.map((item) => ({
          key: item.key,
          label: item.label,
          active: location.pathname === `/tasks/${item.key}`,
          onClick: () => navigate(`/tasks/${item.key}`),
        })),
      },
      {
        key: "messages",
        label: "Messages",
        icon: <MarkUnreadChatAltOutlinedIcon fontSize="small" />,
        active: location.pathname === "/message",
        children: MESSAGE_MENU_ITEMS.map((item) => ({
          key: item.key,
          label: item.label,
          active: location.pathname === "/message" && (new URLSearchParams(location.search).get("section") || "compose") === item.key,
          onClick: () => navigate(`/message?section=${item.key}`),
        })),
      },
      {
        key: "logistic",
        label: "Logistic",
        icon: <LocalShippingOutlinedIcon fontSize="small" />,
        active: location.pathname.startsWith("/logistic"),
        defaultExpanded: true,
        children: LOGISTIC_MENU_ITEMS.map((item) => ({
          key: item.key,
          label: item.label,
          active: location.pathname === `/logistic/${item.key}`,
          onClick: () => navigate(`/logistic/${item.key}`),
        })),
      },
      { key: "groups", label: "Groups", icon: <Groups2OutlinedIcon fontSize="small" />, active: location.pathname.startsWith("/groups"), onClick: () => navigate("/groups") },
      { key: "reminders", label: "Reminders", icon: <EventNoteOutlinedIcon fontSize="small" />, active: location.pathname === "/reminders", onClick: () => navigate("/reminders") },
      { key: "routes", label: "Routes", icon: <RouteOutlinedIcon fontSize="small" />, active: location.pathname === "/routes", onClick: () => navigate("/routes") },
      {
        key: "expense",
        label: "Expense",
        icon: <PaidOutlinedIcon fontSize="small" />,
        active: location.pathname.startsWith("/expense"),
        children: EXPENSE_MENU_ITEMS.map((item) => ({
          key: item.key,
          label: item.label,
          active: location.pathname === item.path,
          onClick: () => navigate(item.path),
        })),
      },
      {
        key: "sensors",
        label: "Sensors",
        icon: <SensorsRoundedIcon fontSize="small" />,
        active: location.pathname.startsWith("/sensors"),
        children: SENSOR_MENU_ITEMS.map((item) => ({
          key: item.key,
          label: item.label,
          active: location.pathname === item.path,
          onClick: () => navigate(item.path),
        })),
      },
      {
        key: "users",
        label: "Users & Roles",
        icon: <PersonOutlineRoundedIcon fontSize="small" />,
        active: location.pathname.startsWith("/users"),
        children: USER_ROLE_ROUTE_CONFIG.map((item) => ({
          key: item.key,
          label: item.label,
          active: location.pathname === item.path,
          onClick: () => navigate(item.path),
        })),
      },
      { key: "billing", label: "Billing & Licenses", icon: <AssessmentOutlinedIcon fontSize="small" />, active: location.pathname === "/licenses", onClick: () => navigate("/licenses") },
      { key: "settings", label: "Settings", icon: <SettingsOutlinedIcon fontSize="small" />, active: location.pathname === "/profile", onClick: () => navigate("/profile") },
    ],
    [location.pathname, location.search, navigate]
  );
  const sidebarItems = useGlobalSidebarItems();
  void sidebarItemsLegacy;

  return (
    <div className="dashboard-page-shell">
      <GlobalDashboardSidebar
        items={sidebarItems}
        profileName={profileName}
        roleLabel={roleLabel}
        onSupport={() => navigate("/message")}
        onLogout={() => dispatch(logout())}
      />

      <main className="dashboard-content" style={{ gap: 18 }}>
        <Box>
          <Typography sx={{ mt: 0.2, color: "#7c8ba1", fontSize: 13.5 }}>
            Logistic / {sectionMeta.title}
          </Typography>
          <Typography sx={{ mt: 1.1, fontSize: { xs: "2rem", md: "2.35rem" }, fontWeight: 900, color: "#0f172a" }}>
            {sectionMeta.title}
          </Typography>
          <Typography sx={{ mt: 0.6, color: "#64748b", fontSize: 15.5 }}>
            {sectionMeta.subtitle}
          </Typography>
        </Box>

        <LogisticsDashboard forcedTabKey={section} hideTabs embedded />
      </main>
    </div>
  );
}
